import { Injectable, Injector } from '@angular/core';
import { BaseService } from './base.service';
import { Observable } from 'rxjs';
import { Commission, CommissionV2, CommissionPatchSelectedSignature } from '../models/commission.model';
import { Stamp, StampV2 } from '../models/stamp.model';
import { map } from 'rxjs/operators';
import { formatDate } from '@angular/common';
import { CommissionInfo, CommissionInfoPatch, CommissionInfosModel } from '../models/commission-info.model';
import { SupportContextService } from './support-context.service';

@Injectable({
  providedIn: 'root'
})
export class CommissionService extends BaseService {
  className = "CommissionService";

  constructor(injector: Injector, private supportContext: SupportContextService) {
    super(injector);
  }

  public getCommission(id: number | null): Observable<Commission> {
    return this.get<Commission[]>(`/v1/commissions/${id}`).pipe(
      map(commissions => commissions[0])
    );
  }

  public getCommissions(isReadDataFromSassEnabled: boolean): Observable<Commission[] | CommissionV2[]> {
    const params = this.supportContext.accountId ? { accountId: this.supportContext.accountId } : {};
    return isReadDataFromSassEnabled ?
      this.get<CommissionV2[]>(`/v2/commissions`, params) :
      this.get<Commission[]>(`/v1/commissions`);
  }

  public getDraftCommissions(): Observable<CommissionInfosModel> {
    const params = this.supportContext.accountId ? { accountId: this.supportContext.accountId } : {};
    return this.get<CommissionInfosModel>(`/v1/commissionInfo/search`, params);
  }

  public saveCommission(commission: CommissionV2): Observable<CommissionV2> {
    return this.post<CommissionV2>('/v2/commissions', commission);

  }

  public saveDraftCommission(commissionInfo: CommissionInfo): Observable<CommissionInfo> {
    return this.post<CommissionInfo>('/v1/commissionInfo', commissionInfo);
  }

  public updateCommission(isReadDataFromSassEnabled: boolean, commission: Commission | CommissionV2): Observable<Commission | CommissionV2> {
    if (isReadDataFromSassEnabled) {
      const commissionV2 = commission as CommissionV2;
      return this.put<CommissionV2>(`/v2/commissions/${commissionV2.sassGuid}`, commissionV2);
    } else {
      const commissionV1 = commission as Commission;
      return this.patch<Commission>(`/v1/commissions/${commissionV1.id}`, commissionV1);
    }
  }

  public updateDraftCommission(commissionInfoPatch: CommissionInfo): Observable<CommissionInfo> {
    return this.patch<CommissionInfo>(`/v1/commissionInfo`, commissionInfoPatch);
  }

  public patchDraftCommission(commissionInfoPatch: CommissionInfoPatch): Observable<CommissionInfoPatch> {
    return this.patch<CommissionInfoPatch>(`/v1/commissionInfo`, commissionInfoPatch);
  }

  public updateSelectedSignature(sassCommissionPatch: CommissionPatchSelectedSignature): Observable<CommissionPatchSelectedSignature> {
    return this.patch<CommissionPatchSelectedSignature>(`/v2/commissions`, sassCommissionPatch);
  }

  public deleteCommission(isReadDataFromSassEnabled: boolean, commission: Commission | CommissionV2): Observable<object> {
    if (isReadDataFromSassEnabled) {
      const commissionV2 = commission as CommissionV2;
      return this.delete(`/v2/commissions/${commissionV2.sassGuid}`);
    } else {
      const commissionV1 = commission as Commission;
      return this.delete(`/v1/commissions/${commissionV1.id}`);
    }
  }

  public deleteDraftCommission(id: number): Observable<object> {
    return this.delete(`/v1/commissionInfo/${id}`);
  }

  public getStampCreatorStates(): Observable<string> {
    return this.getText('/v1/config/stampCreator');
  }

  public getOrGenerateStamp(
    stateCode: string,
    countyName: string,
    commissionExpiration: any,
    commissionName: string,
    commissionNumber: string,
    isReadDataFromSassEnabled: boolean = false
  ): Observable<Stamp | StampV2[]> {
    const formattedDate = commissionExpiration
      ? formatDate(commissionExpiration, 'MMM. dd, yyyy', 'en-US')
      : '';

    const params = {
      stateCode,
      countyName,
      commissionExpiration: formattedDate,
      commissionName,
      commissionNumber
    };

    return isReadDataFromSassEnabled
      ? this.get<StampV2[]>('/v1/stamp/list', params)
      : this.get<Stamp>('/v1/stamp', params);
  }
}
